import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Container, FormGroup, Input, Row, UncontrolledTooltip } from 'reactstrap';
import { mapValues, pick } from 'lodash';
import { addDays, formatISO, parseISO } from 'date-fns';
import { PaymentMethod } from '@payer-admin-ui/api/src/invoices/models/PaymentMethod';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { Role } from '@payer-admin-ui/components-v2/src/types/Role';
import { ExpandableContainer } from '../../ExpandableContainer/ExpandableContainer';
import { TextInput } from '../../Input/TextInput';
import { PayerDropdown } from '../../Dropdowns/PayerDropdown';
import { CheckboxGroup } from '../../Checkbox/CheckboxGroup';
import { PayerCheckbox } from '../../Checkbox/PayerCheckbox';
import { FormSection } from '../../FormSection/FormSection';
import { RoleCheck } from '../../RoleCheck/RoleCheck';
import styles from './UpdateInvoiceCustomerCard.module.scss';

const UpdateInvoiceDetailsCard = ({ invoice, patchInvoice, disabled, expanded }) => {
  const { t } = useTranslation();

  const {
    orderReferenceId,
    yourReference,
    site,
    additionalReferenceId,
    description,
    comment,
    yourOrderNumber,
    currencyCode,
    type,
    deliveryType,
    paymentMethods,
    invoiceDate,
    dueDate,
    invoicePeriod,
    partOfInvoiceAggregation,
  } = invoice ?? {};
  const { startDate: invoicePeriodStart, endDate: invoicePeriodEnd } = invoicePeriod ?? {};
  return (
    <ExpandableContainer
      header={<b>{t('components.cards.invoice.invoiceDetails.title')}</b>}
      defaultExpanded={expanded ?? true}
    >
      {invoice && (
        <Container className={styles.InvoiceCustomerCard}>
          <Row className={styles.section}>
            <Col xs="12" md="6">
              <h5>{t('components.cards.invoice.invoiceDetails.invoiceDate')}</h5>
              <Input
                type="date"
                id="invoiceDate"
                value={invoiceDate ?? ''}
                onChange={event =>
                  patchInvoice({
                    invoiceDate: event.target.value,
                    dueDate: formatISO(addDays(parseISO(event.target.value), 30), { representation: 'date' }),
                  })
                }
                disabled={true}
              />
            </Col>
            <Col xs="12" md="6">
              <h5>{t('components.cards.invoice.invoiceDetails.dueDate')}</h5>
              <Input
                type="date"
                id="dueDate"
                value={dueDate ?? ''}
                onChange={event => patchInvoice({ dueDate: event.target.value })}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" md="6">
              <h5>{t('components.cards.invoice.invoiceDetails.invoicePeriod.startDate')}</h5>
              <Input
                type="date"
                id="invoicePeriodStart"
                value={invoicePeriodStart ?? ''}
                onChange={event => patchInvoice({ invoicePeriod: { startDate: event.target.value } })}
                disabled={true}
              />
            </Col>
            <Col xs="12" md="6">
              <h5>{t('components.cards.invoice.invoiceDetails.invoicePeriod.endDate')}</h5>
              <Input
                type="date"
                id="invoicePeriodEnd"
                value={invoicePeriodEnd ?? ''}
                onChange={event => patchInvoice({ invoicePeriod: { endDate: event.target.value } })}
                disabled={true}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" md="4">
              <h5>{t('components.cards.invoice.invoiceDetails.currencyCode')}</h5>
              <PayerDropdown
                items={CurrencyCode}
                selectedItem={currencyCode}
                noSelectedItemText={t('components.cards.invoice.invoiceDetails.noCurrencyCode')}
                onItemSelect={currencyCode => patchInvoice({ currencyCode })}
                disabled={true}
              />
            </Col>
            <Col xs="12" md="4">
              <h5>{t('components.cards.invoice.invoiceDetails.type')}</h5>
              <PayerDropdown
                items={pick(t('enums.invoiceType.values', { returnObjects: true }), ['NORMAL', 'PREPAYMENT'])}
                selectedItem={type}
                onItemSelect={type => patchInvoice({ type })}
                disabled={true}
              />
            </Col>
            <Col xs="12" md="4">
              <h5>{t('components.cards.invoice.invoiceDetails.deliveryType')}</h5>
              <PayerDropdown
                items={t('enums.deliveryType.values', { returnObjects: true })}
                selectedItem={deliveryType}
                onItemSelect={deliveryType => patchInvoice({ deliveryType })}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.orderReferenceId')}
                value={orderReferenceId}
                setValue={orderReferenceId => patchInvoice({ orderReferenceId })}
                validators={[Validators.notEmpty, Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.yourOrderNumber')}
                value={yourOrderNumber}
                setValue={yourOrderNumber => patchInvoice({ yourOrderNumber })}
                validators={[Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.additionalReferenceId')}
                value={additionalReferenceId}
                setValue={additionalReferenceId => patchInvoice({ additionalReferenceId })}
                validators={[Validators.length(0, 48)]}
                disabled={true}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.yourReference')}
                value={yourReference}
                setValue={yourReference => patchInvoice({ yourReference })}
                validators={[Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.site')}
                value={site}
                setValue={site => patchInvoice({ site })}
                validators={[Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.description')}
                value={description}
                setValue={description => patchInvoice({ description })}
                disabled={true}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <TextInput
                title={t('components.cards.invoice.invoiceDetails.comment')}
                value={comment}
                setValue={comment => patchInvoice({ comment })}
                validators={[Validators.length(0, 128)]}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs={12} md={12} lg={12}>
              <CheckboxGroup
                title={t('components.cards.invoice.invoiceDetails.paymentMethods')}
                items={mapValues(PaymentMethod, value => t(`enums.paymentMethod.values.${value}`))}
                selectedItems={paymentMethods}
                setSelectedItems={paymentMethods => patchInvoice({ paymentMethods })}
                disabled={false}
              />
            </Col>
          </Row>
          <RoleCheck requiredRole={Role.INVOICE_STATEMENT_ACCESS}>
            <Row className={styles.section}>
              <Col xs={12} md={12} lg={12}>
                <FormSection title={t('components.cards.invoice.invoiceDetails.invoiceStatement')} lg={12}>
                  <FormGroup style={{ display: 'flex' }}>
                    <PayerCheckbox
                      id={t('components.cards.invoice.invoiceDetails.includeInInvoiceStatement')}
                      key={t('components.cards.invoice.invoiceDetails.includeInInvoiceStatement')}
                      label={t('components.cards.invoice.invoiceDetails.includeInInvoiceStatement')}
                      onChange={checked =>
                        patchInvoice({
                          partOfInvoiceAggregation: checked,
                          invoiceDate: checked ? undefined : invoiceDate,
                          dueDate: checked ? undefined : dueDate,
                        })
                      }
                      checked={partOfInvoiceAggregation}
                      disabled={disabled}
                    />
                    <i id="invoice-aggregation-info" className="fa fa-info-circle" aria-hidden="true" />
                    <UncontrolledTooltip placement="top" target="invoice-aggregation-info" trigger="hover" delay={100}>
                      {t('invoices.CreateInvoicePage.invoiceStatementInfo')}
                    </UncontrolledTooltip>
                  </FormGroup>
                </FormSection>
              </Col>
            </Row>
          </RoleCheck>
        </Container>
      )}
    </ExpandableContainer>
  );
};

UpdateInvoiceDetailsCard.propTypes = {
  invoice: PropTypes.shape({
    orderReferenceId: PropTypes.string,
    yourReference: PropTypes.string,
    site: PropTypes.string,
    additionalReferenceId: PropTypes.string,
    description: PropTypes.string,
    comment: PropTypes.string,
    yourOrderNumber: PropTypes.string,
    currencyCode: PropTypes.string,
    type: PropTypes.string,
    deliveryType: PropTypes.string,
    paymentMethods: PropTypes.arrayOf(PropTypes.string),
    invoiceDate: PropTypes.string,
    dueDate: PropTypes.string,
    invoicePeriod: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
    partOfInvoiceAggregation: PropTypes.bool,
  }),
  patchInvoice: PropTypes.func,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
};

export { UpdateInvoiceDetailsCard };
